<script setup>
import Icon from "../Common/Icon.vue";
import ProBanner from "../Subscription/ProBanner.vue";
import { formatTimestamp } from "../../helpers/date";
import { useAuthStore } from "../../stores/auth";
import { useSubscriptionStore } from "../../stores/subscription";
import { useFilterStore } from "../../stores/filter";
import { computed } from "vue";

const authStore = useAuthStore();
const subscriptionStore = useSubscriptionStore();
const filterStore = useFilterStore();

const emit = defineEmits(["reached-end"]);

const props = defineProps({
  articles: {
    type: Array,
    required: true,
  },
  headlineTag: {
    type: String,
    default: "h2",
  },
});

const showProBanner = computed(
  () => authStore.isAuthenticated && !subscriptionStore.hasSubscription && filterStore.isAnyFilterActive,
);

function isAlreadySeen(article) {
  return window.klarnachrichten.articleHistory.isInHistory(article.id.toString());
}
</script>

<template>
  <div>
    <div class="space-y-8 md:space-y-16 lg:space-y-20 relative">
      <template
        v-for="(article, index) in articles"
        :key="article.id"
      >
      <ProBanner v-if="showProBanner && index === 2" />
      <div
        class="group relative flex flex-col lg:grid grid-cols-2 xxl:grid-cols-7 pt-6 md:pt-8 border-t border-dark/50 items-start gap-x-24 xl:gap-x-32"
      >
        <div class="text-12 font-medium flex items-center flex-wrap gap-x-2 col-start-1 xxl:col-end-4 mb-2">
          <div v-for="category in article.categories" :key="category" class="text-primary flex items-center gap-x-2">
            <p v-text="category"></p>
            <div class="size-1 bg-dark rounded-lg"></div>
          </div>
          <time
            class="text-dark/70"
            :datetime="formatTimestamp(article.timestamp, 'iso')"
            v-text="formatTimestamp(article.timestamp, 'D. MMMM YYYY, HH:mm')"
          ></time>
        </div>

        <component
          :is="props.headlineTag"
          v-html="article.title"
          class="col-start-1 xxl:col-end-4 font-bold leading-tight text-25 lg:text-31 mb-4 lg:mb-6"
        ></component>

        <a
          :href="article.link"
          class="expand-link-area col-start-1 xxl:col-end-4 order-last mt-4 lg:mt-0 text-primary font-medium text-16 flex items-center gap-x-4"
        >
          <span v-t="'article-list.article.link'"></span>
          <Icon name="arrow" class="size-4 shrink-0 group-hover:translate-x-1 transition-transform" />
        </a>

        <div
          class="w-fit bg-primary/10 text-primary text-12 px-3 py-1 mb-6 lg:mb-0 lg:-mt-2 -order-1 flex items-center gap-x-3 lg:col-start-2 xxl:col-start-4 xxl:col-end-7 lg:row-start-1"
          v-if="isAlreadySeen(article)"
        >
          <Icon name="eye" class="size-4 shrink-0" />
          <p v-t="'article-list.article.already-seen'"></p>
        </div>

        <div
          v-html="article.teaser"
          class="text-16 lg:text-20 max-w-lg lg:col-start-2 xxl:col-start-4 xxl:col-end-7 lg:row-start-2"
        ></div>
      </div>
      </template>
    </div>
  </div>
</template>
